/**
 * Wishlist functionality using localStorage
 * Compatible with Vitals Wishlist (vtl_wl key)
 */
(function() {
  const STORAGE_KEY = 'vtl_wl';

  // Get wishlist from localStorage
  function getWishlist() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      console.error('Error reading wishlist:', e);
      return [];
    }
  }

  // Save wishlist to localStorage
  function saveWishlist(wishlist) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(wishlist));
      // Dispatch event for other components (Vitals compatibility)
      window.dispatchEvent(new CustomEvent('vtl:wishlist:updated'));
    } catch (e) {
      console.error('Error saving wishlist:', e);
    }
  }

  // Check if product is in wishlist by handle
  function isInWishlist(productHandle) {
    const wishlist = getWishlist();
    return wishlist.some(item => item.handle === productHandle);
  }

  // Toggle product in wishlist
  function toggleWishlist(productHandle) {
    const wishlist = getWishlist();
    const existingIndex = wishlist.findIndex(item => item.handle === productHandle);

    if (existingIndex === -1) {
      // Add to wishlist
      wishlist.push({
        handle: productHandle,
        added_at: Date.now()
      });
      saveWishlist(wishlist);
      return true; // Added
    } else {
      // Remove from wishlist
      wishlist.splice(existingIndex, 1);
      saveWishlist(wishlist);
      return false; // Removed
    }
  }

  // Update button visual state
  function updateButtonState(button, isActive) {
    if (isActive) {
      button.classList.add('is-active');
      button.setAttribute('aria-pressed', 'true');
    } else {
      button.classList.remove('is-active');
      button.setAttribute('aria-pressed', 'false');
    }
  }

  // Initialize all wishlist buttons on page
  function initWishlistButtons() {
    const buttons = document.querySelectorAll('.product-card__wishlist-btn');

    buttons.forEach(button => {
      const productHandle = button.dataset.productHandle;
      if (!productHandle) return;

      // Set initial state
      const isActive = isInWishlist(productHandle);
      updateButtonState(button, isActive);

      // Remove existing listener to prevent duplicates
      button.removeEventListener('click', handleWishlistClick);
      button.addEventListener('click', handleWishlistClick);
    });
  }

  // Handle wishlist button click
  function handleWishlistClick(e) {
    e.preventDefault();
    e.stopPropagation();

    const button = e.currentTarget;
    const productHandle = button.dataset.productHandle;

    if (!productHandle) return;

    const isNowActive = toggleWishlist(productHandle);
    updateButtonState(button, isNowActive);

    // Update all buttons for the same product (in case it appears multiple times)
    document.querySelectorAll(`.product-card__wishlist-btn[data-product-handle="${productHandle}"]`).forEach(btn => {
      updateButtonState(btn, isNowActive);
    });

    // Dispatch custom event for other components to react
    document.dispatchEvent(new CustomEvent('wishlist:change', {
      detail: {
        productHandle,
        isInWishlist: isNowActive,
        wishlist: getWishlist()
      }
    }));
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWishlistButtons);
  } else {
    initWishlistButtons();
  }

  // Re-initialize when new content is added (for infinite scroll, quick view, etc.)
  let wishlistInitPending = false;
  const observer = new MutationObserver((mutations) => {
    if (wishlistInitPending) return;
    let shouldInit = false;
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === 1 && (
          node.classList?.contains('product-card__wishlist-btn') ||
          node.querySelector?.('.product-card__wishlist-btn')
        )) {
          shouldInit = true;
          break;
        }
      }
      if (shouldInit) break;
    }
    if (shouldInit) {
      wishlistInitPending = true;
      requestAnimationFrame(() => {
        initWishlistButtons();
        wishlistInitPending = false;
      });
    }
  });

  // Only observe product grid containers, not the entire body
  function observeProductContainers() {
    const containers = document.querySelectorAll('.product-grid, .resource-list, .collection-list, [data-product-grid]');
    if (containers.length > 0) {
      containers.forEach(c => observer.observe(c, { childList: true, subtree: true }));
    } else {
      // Fallback: observe body but only childList on direct children
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeProductContainers);
  } else {
    observeProductContainers();
  }

  // Listen for Vitals wishlist updates (from product page)
  window.addEventListener('vtl:wishlist:updated', initWishlistButtons);

  // Expose API globally
  window.BodhiWishlist = {
    getWishlist,
    isInWishlist,
    toggleWishlist,
    initWishlistButtons
  };
})();
